import React, { useCallback, useEffect, useState } from 'react';
import { useBookingApi } from './bookingApi';
import {
    EXCEPTION_KINDS,
    EXCEPTION_KIND_LABELS,
    calendarDateTimeInputToIso,
    formatInterval,
    toCalendarDateTimeInput,
} from './bookingFormat';

/**
 * Blackout / opening / override manager for a single resource. Blackouts
 * subtract availability; openings add ad-hoc bookable time (the "arbitrary
 * openings" use case); overrides replace it. Semi-independent — fetches its own
 * exceptions for the given resource.
 */
function defaultDraft(timezone) {
    const date = toCalendarDateTimeInput(new Date(), timezone).slice(0, 10);
    return {
        kind: 'blackout',
        startAt: `${date}T09:00`,
        endAt: `${date}T17:00`,
        note: '',
    };
}

const previewReasonCopy = (reason, product) => {
    const duration = product?.durationMinutes;
    const bufferAfter = product?.bufferAfterMinutes || 0;
    switch (reason) {
        case 'openings-too-short':
            return `This opening is shorter than the ${duration}-minute appointment`
                + `${bufferAfter ? ` plus its ${bufferAfter}-minute turnaround buffer` : ''}.`
                + ' Lengthen the opening or reduce the appointment duration/buffer.';
        case 'no-availability-authored':
            return 'No weekly hours or openings are set for this calendar yet.'
                + ' Add weekly hours, or an opening above, before shoppers can book.';
        case 'min-notice':
            return 'Upcoming times exist but are hidden by this product\'s minimum-notice'
                + ' window — same-day (and near-term) slots will not show to shoppers.';
        case 'fully-booked':
            return 'Every opening that fits this appointment is already taken by existing bookings.';
        case 'beyond-horizon':
            return 'No bookable times fall within this product\'s booking window.';
        default:
            return 'No bookable slots could be previewed for this calendar.';
    }
};

function BookableSlotPreview({ resource, api }) {
    const [products, setProducts] = useState([]);
    const [productUuid, setProductUuid] = useState('');
    const [result, setResult] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [loadedProducts, setLoadedProducts] = useState(false);

    const activeProduct = products.find(p => p.uuid === productUuid) || null;

    const runPreview = useCallback(async (uuid) => {
        if (!uuid) return;
        setLoading(true);
        setError(null);
        try {
            setResult(await api.previewSlots(resource.id, { productId: uuid, count: 5 }));
        } catch (err) {
            setError(err.message || 'Could not preview slots.');
            setResult(null);
        } finally {
            setLoading(false);
        }
    }, [api, resource.id]);

    const handleOpen = useCallback(async () => {
        setError(null);
        let list = products;
        if (!loadedProducts) {
            setLoading(true);
            try {
                list = await api.listBookableProducts(resource.id);
                setProducts(list);
                setLoadedProducts(true);
            } catch (err) {
                setError(err.message || 'Could not load bookable products.');
                setLoading(false);
                return;
            }
            setLoading(false);
        }
        if (list.length === 0) {
            setResult(null);
            setError('No bookable appointment products use this calendar yet.');
            return;
        }
        const uuid = list.length === 1 ? list[0].uuid : (productUuid || '');
        setProductUuid(uuid);
        if (uuid) runPreview(uuid);
    }, [api, resource.id, products, loadedProducts, productUuid, runPreview]);

    return (
        <div className="booking-preview">
            <div className="booking-preview-actions">
                <button
                    type="button"
                    className="button"
                    onClick={handleOpen}
                    disabled={loading}
                    aria-label={`Preview bookable slots for ${activeProduct?.name || 'products'} on ${resource.name}`}
                >
                    {loading ? 'Checking…' : 'Preview bookable slots'}
                </button>
                {loadedProducts && products.length > 1 && (
                    <select
                        value={productUuid}
                        onChange={(e) => { setProductUuid(e.target.value); runPreview(e.target.value); }}
                    >
                        <option value="">Choose a product…</option>
                        {products.map(product => (
                            <option key={product.uuid} value={product.uuid}>{product.name}</option>
                        ))}
                    </select>
                )}
            </div>

            {error && <p className="booking-muted booking-preview-warning">{error}</p>}

            {result && result.slots.length > 0 && (
                <ul className="booking-preview-list">
                    {result.slots.map(slot => (
                        <li key={`${slot.resourceId}-${slot.startAt}`}>
                            {formatInterval(slot.startAt, slot.endAt, resource.timezone)}
                        </li>
                    ))}
                </ul>
            )}

            {result && result.slots.length === 0 && result.reason && (
                <p className="error booking-preview-warning" role="status" aria-live="polite">
                    {previewReasonCopy(result.reason, activeProduct)}
                </p>
            )}
        </div>
    );
}

export default function ExceptionsManager({ resource }) {
    const api = useBookingApi();
    const [exceptions, setExceptions] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [draft, setDraft] = useState(() => defaultDraft(resource.timezone));
    const [adding, setAdding] = useState(false);
    const [busyId, setBusyId] = useState(null);

    const load = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            setExceptions(await api.listExceptions(resource.id));
        } catch (err) {
            setError(err.message || 'Could not load exceptions.');
        } finally {
            setLoading(false);
        }
    }, [api, resource.id]);

    useEffect(() => { load(); }, [load]);

    const setField = (name, value) => setDraft(prev => ({ ...prev, [name]: value }));

    const handleAdd = async (event) => {
        event.preventDefault();
        setError(null);
        const startAt = calendarDateTimeInputToIso(draft.startAt, resource.timezone);
        const endAt = calendarDateTimeInputToIso(draft.endAt, resource.timezone);
        if (!startAt || !endAt || new Date(startAt) >= new Date(endAt)) {
            setError('Choose a valid start and end, with the end after the start.');
            return;
        }
        setAdding(true);
        try {
            const created = await api.createException(resource.id, {
                kind: draft.kind,
                startAt,
                endAt,
                note: draft.note.trim() || null,
            });
            setExceptions(prev => [...prev, created].sort((a, b) => new Date(a.startAt) - new Date(b.startAt)));
            setDraft(defaultDraft(resource.timezone));
        } catch (err) {
            setError(err.message || 'Could not add this exception.');
        } finally {
            setAdding(false);
        }
    };

    const handleDelete = async (exception) => {
        setBusyId(exception.id);
        setError(null);
        try {
            await api.deleteException(resource.id, exception.id);
            setExceptions(prev => prev.filter(e => e.id !== exception.id));
        } catch (err) {
            setError(err.message || 'Could not remove this exception.');
        } finally {
            setBusyId(null);
        }
    };

    return (
        <div className="booking-exceptions">
            <p className="help">
                Blackouts close time that would otherwise be available; openings add
                ad-hoc bookable time; overrides replace the weekly hours for a span.
            </p>

            <BookableSlotPreview resource={resource} api={api} />

            {error && <div className="error booking-inline-error">{error}</div>}

            <form className="booking-exception-form" onSubmit={handleAdd}>
                <div className="booking-field">
                    <span>Type</span>
                    <select value={draft.kind} onChange={e => setField('kind', e.target.value)}>
                        {EXCEPTION_KINDS.map(kind => (
                            <option key={kind} value={kind}>{EXCEPTION_KIND_LABELS[kind]}</option>
                        ))}
                    </select>
                </div>
                <div className="booking-field">
                    <span>Start</span>
                    <input
                        type="datetime-local"
                        value={draft.startAt}
                        onChange={e => setField('startAt', e.target.value)}
                    />
                </div>
                <div className="booking-field">
                    <span>End</span>
                    <input
                        type="datetime-local"
                        value={draft.endAt}
                        onChange={e => setField('endAt', e.target.value)}
                    />
                </div>
                <div className="booking-field booking-field--grow">
                    <span>Note <small className="help">(optional)</small></span>
                    <input
                        type="text"
                        value={draft.note}
                        onChange={e => setField('note', e.target.value)}
                        placeholder="e.g. Holiday, maintenance"
                        maxLength={255}
                    />
                </div>
                <button type="submit" className="button primary" disabled={adding}>
                    {adding ? 'Adding…' : 'Add'}
                </button>
            </form>
            <p className="help booking-tz-note">
                Times are entered in this calendar's timezone ({resource.timezone}).
            </p>

            {loading ? (
                <div className="booking-state booking-state--loading">
                    <i className="fas fa-spinner fa-spin" aria-hidden="true"></i>
                    <span>Loading exceptions…</span>
                </div>
            ) : exceptions.length === 0 ? (
                <p className="booking-muted">No blackouts or openings yet.</p>
            ) : (
                <ul className="booking-exception-list">
                    {exceptions.map(exception => (
                        <li key={exception.id} className={`booking-exception booking-exception--${exception.kind}`}>
                            <span className={`booking-badge ${exception.kind === 'blackout' ? 'bad' : 'ok'}`}>
                                {EXCEPTION_KIND_LABELS[exception.kind] || exception.kind}
                            </span>
                            <div className="booking-exception-main">
                                <span>{formatInterval(exception.startAt, exception.endAt, resource.timezone)}</span>
                                {exception.note && <small className="booking-muted">{exception.note}</small>}
                            </div>
                            <button
                                type="button"
                                className="booking-iconbtn"
                                onClick={() => handleDelete(exception)}
                                disabled={busyId === exception.id}
                                aria-label="Remove exception"
                            >
                                <i className="fas fa-trash" aria-hidden="true"></i>
                            </button>
                        </li>
                    ))}
                </ul>
            )}
        </div>
    );
}
